import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import AiAssistantIcon from "../assets/images/ai_assistant.svg";
import AiAssistantCrossIcon from "../assets/images/ai_assistant_cross.svg";
import AiAssistantCloseIcon from "../assets/images/ai_assistant_close_icon.svg";
import "./ChatAssistant.scss";
import ChatboxBody from "./ChatboxBody";
import ChatboxHeader from "./ChatboxHeader";
import ChatSidebar from "./ChatSidebar";
import { useDispatch } from "react-redux";
import constants from "../constants";
import {
  getSessionChat,
  updateAiToggleToHeader,
} from "repositories/ai-assistant-repository";
import { handleError, handleSuccess } from "utils";
import { getProfile } from "actions";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

/** Stable no-op for Framer Motion callbacks (avoids new function identity each render). */
const noop = () => {};

function IsAccessibleChatAssitant({
  dropdownAiChatOpen,
  setDropdownAiChatOpen,
  children,
}) {
  const { isStaff, isOwner, commonOwnerData, selectedId, profile } =
    useAccountOwnerCommon();
  if ((isOwner || isStaff) && commonOwnerData?.isAiSettingsEnabled) {
    if (children) {
      return children;
    }
    return (
      <ChatAssistant
        dropdownAiChatOpen={dropdownAiChatOpen}
        setDropdownAiChatOpen={setDropdownAiChatOpen}
        profile={profile}
        selectedOwnerId={selectedId}
      />
    );
  }

  return null;
}

function ChatAssistant({
  dropdownAiChatOpen,
  setDropdownAiChatOpen,
  profile,
  selectedOwnerId,
}) {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAssistantCard, setShowAssistantCard] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionPageNumber, setSessionPageNumber] = useState(1);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [chatSessionId, setchatSessionId] = useState(0);
  const [sessionChatLoading, setSessionChatLoading] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [canHandleScroll, setCanHandleScroll] = useState(false);
  const [showFab, setShowFab] = useState(true);
  const fabRef = React.useRef(null);
  const [exitY, setExitY] = useState(-28);

  useEffect(() => {
    if (typeof dropdownAiChatOpen !== "undefined") {
      setIsOpen(!!dropdownAiChatOpen);
    }
  }, [dropdownAiChatOpen]);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isExpanded]);

  useEffect(() => {
    if (fabRef.current) {
      const rect = fabRef.current.getBoundingClientRect();
      const toTopMargin = 16;
      const delta = Math.max(0, rect.top - toTopMargin);
      setExitY(-delta);
    }
  }, [isOpen, showFab, profile?.showWebAiToggleInHeader]);

  useEffect(() => {
    if (sessionChatLoading) setCanHandleScroll(false);
  }, [sessionChatLoading]);

  useEffect(() => {
    if (!profile?.showWebAiToggleInHeader) {
      setShowFab(true);
    }
  }, [profile?.showWebAiToggleInHeader]);

  const fetchSessionChat = useCallback(
    async (sessionId, pageNumber) => {
      if (sessionChatLoading) return;

      if (pageNumber !== 1 && messages?.length == totalMessageCount) return;

      try {
        setSessionChatLoading(true);
        setShowAssistantCard(false);

        if (!isExpanded) setShowSidebar(false);
        const pageNo = pageNumber ?? sessionPageNumber;
        if (pageNo == 1) setMessages([]);
        const params = {
          pageNo,
          pageSize: constants.pageSize,
          AiChatSessionId: sessionId,
        };
        const resp = await getSessionChat(params);

        const mapped = (resp?.items || [])
          .map((val) => ({
            id: val?.id,
            messageId: val?.id,
            parentMessageId: val?.parentMessageId,
            text: val?.message || "",
            sender: val?.sender || "Ai",
            createdAt: val?.createdAt,
            ratings: val?.ratings,
            reports: val?.reports,
            filePaths: val?.filePaths || [],
            attachments: val?.filePaths
              ? val.filePaths.map((filePath, index) => ({
                  id: `attachment-${val.id}-${index}`,
                  name: filePath.split("/").pop() || `file-${index}`,
                  type:
                    filePath.includes(".jpg") ||
                    filePath.includes(".jpeg") ||
                    filePath.includes(".png")
                      ? "image/jpeg"
                      : "application/pdf",
                  blobUrl: filePath,
                  preview: filePath,
                }))
              : [],
          }))
          .sort(
            (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
          );

        if (pageNo == 1) {
          setMessages(mapped);
        } else {
          setMessages((prev) => [...mapped, ...prev]);
        }
        setSessionPageNumber(pageNo);
        setchatSessionId(sessionId);
        setTotalMessageCount(resp.totalCount);
        setTimeout(() => {
          setCanHandleScroll(true);
        }, 2000);
      } catch (error) {
        handleError(error);
      } finally {
        setSessionChatLoading(false);
      }
    },
    [
      sessionChatLoading,
      messages?.length,
      totalMessageCount,
      sessionPageNumber,
      isExpanded,
    ]
  );

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setchatSessionId(0);
    setTotalMessageCount(0);
    setSessionPageNumber(1);
    setShowAssistantCard(true);
    setSelectedChatId(null);
    if (window.innerWidth <= 600 || !isExpanded) {
      setShowSidebar(false);
    }
  }, [isExpanded]);

  const updateAiChatToHeader = useCallback(async (e) => {
    e.stopPropagation();
    setShowFab(false);

    try {
      const res = await updateAiToggleToHeader(true);
      handleSuccess(res?.message);
      dispatch(getProfile(undefined, { silent: true }));
    } catch (error) {
      handleError(error);
    }
  }, [dispatch]);

  // Memoized callback functions to prevent unnecessary re-renders
  const handleFetchSessionChat = useCallback(
    async (sessionId) => {
      await fetchSessionChat(sessionId, sessionPageNumber + 1);
    },
    [sessionPageNumber, fetchSessionChat]
  );

  const handleReloadSessionChat = useCallback(
    async (sessionId) => {
      setCanHandleScroll(false);
      await fetchSessionChat(sessionId, 1);
    },
    [fetchSessionChat]
  );

  const handleToggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleCloseChat = useCallback(() => {
    setIsOpen((prev) => !prev);
    if (dropdownAiChatOpen) {
      setDropdownAiChatOpen(false);
    }
  }, [dropdownAiChatOpen, setDropdownAiChatOpen]);

  const handleCloseExpanded = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const content = (
    <div className="chat-assistant">
      {isExpanded && (
        <div className="chat-overlay" onClick={handleCloseExpanded}></div>
      )}
      {isOpen && (
        <div className={`chatbox ${isExpanded ? "expanded" : ""}`}>
          <ChatboxHeader
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            profile={profile}
          />

          <div className={`chatbox-body ${isExpanded ? "expanded" : ""}`}>
            <ChatboxBody
              showAssistantCard={showAssistantCard}
              setShowAssistantCard={setShowAssistantCard}
              messages={messages}
              setMessages={setMessages}
              selectedOwnerId={selectedOwnerId}
              totalMessageCount={totalMessageCount}
              chatSessionId={chatSessionId}
              setchatSessionId={setchatSessionId}
              fetchSessionChat={handleFetchSessionChat}
              reloadSessionChat={handleReloadSessionChat}
              sessionChatLoading={sessionChatLoading}
              profile={profile}
              canHandleScroll={canHandleScroll}
              sessionPageNumber={sessionPageNumber}
              isExpanded={isExpanded}
            />
            {showSidebar && (
              <ChatSidebar
                showSidebar={showSidebar}
                selectedOwnerId={selectedOwnerId}
                fetchSessionChat={handleReloadSessionChat}
                selectedChatId={selectedChatId}
                setSelectedChatId={setSelectedChatId}
                handleNewChat={handleNewChat}
                setShowSidebar={setShowSidebar}
              />
            )}
          </div>
        </div>
      )}

      {!isOpen && !profile?.showWebAiToggleInHeader && (
        <AnimatePresence mode="wait">
          {showFab && (
            <motion.div
              key="chat-fab"
              className="chat-icon"
              ref={fabRef}
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: exitY, x: -150, scale: 0.96 }}
              transition={{
                type: "spring",
                stiffness: 140,
                damping: 24,
                mass: 0.9,
              }}
              onAnimationComplete={noop}
            >
              <img
                src={AiAssistantIcon}
                alt="Ai Assistant"
                onClick={handleToggleOpen}
              />
              <img
                src={AiAssistantCrossIcon}
                alt="Ai Assistant Cross"
                className="ai_assistant_cross"
                onClick={updateAiChatToHeader}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {isOpen && (
        <div className="chat-icon" onClick={handleCloseChat}>
          <img src={AiAssistantCloseIcon} alt="Ai Assistant Close" />
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
}

export default IsAccessibleChatAssitant;
